import React, { useMemo } from 'react';
import ImageDisplay from '../ImageDisplay/ImageDisplay';
import { generateAvatarUrl } from '../../utils/avatar';
var DEFAULT_STATIC_FALLBACK = '/images/account_circle_24dp_1F1F1F.png';
// Helper function to get size in pixels
var getSizeInPx = function(size) {
    if (typeof size === 'number') return size;
    var sizeMap = {
        xs: 24,
        sm: 32,
        md: 40,
        lg: 48,
        xl: 64,
        '2xl': 96,
        '3xl': 128,
        '4xl': 160
    };
    // Fallback to 'md' size if invalid string is provided
    return sizeMap[size] || sizeMap.md;
};
// Helper function to get border color value
var getBorderColorValue = function(borderColor) {
    var colorMap = {
        primary: "#3B82F6",
        success: "#22C55E",
        warning: "#F59E0B",
        danger: "#EF4444",
        default: "#E5E7EB"
    };
    return colorMap[borderColor] || colorMap.default;
};
// Helper function to generate avatar styles
var getAvatarStyles = function(size, border, borderColor) {
    var styles = {
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'inline-block',
        position: 'relative',
        verticalAlign: 'middle'
    };
    var finalSize = size === undefined ? 'md' : size; // Default to 'md' if undefined
    var sizePx = getSizeInPx(finalSize);
    styles.width = "".concat(sizePx, "px");
    styles.height = "".concat(sizePx, "px");
    var finalBorder = border === undefined ? 'none' : border; // Default to 'none'
    var finalBorderColor = borderColor === undefined ? 'default' : borderColor; // Default to 'default'
    var borderWidth = finalBorder === 'thin' ? 1 : finalBorder === 'thick' ? 2 : 0;
    if (borderWidth > 0) {
        var color = getBorderColorValue(finalBorderColor);
        // Tailwind's 'ring' uses box-shadow for outline effect
        styles.boxShadow = "0 0 0 ".concat(borderWidth, "px ").concat(color);
    }
    return styles;
};
var Avatar = function(param) {
    var username = param.username, name = param.name, profileImage = param.profileImage, size = param.size, border = param.border, borderColor = param.borderColor, _param_style = param.style, style = _param_style === void 0 ? 'avataaars' : _param_style, alt = param.alt, className = param.className, onError = param.onError;
    var displayName = name || username || "unknown";
    // username이 문자열인지 확인하고, 아닐 경우 안전하게 변환
    var usernameString = typeof username === 'string' ? username : String(username || '');
    var safeSeed = usernameString.trim() ? usernameString : (displayName === null || displayName === void 0 ? void 0 : displayName.trim()) ? displayName : "unknown";
    // console.log(`[Avatar Debug - ${username}] Input Props: profileImage='${profileImage}', username='${username}', name='${name}'`);
    var dicebearFallbackUrl = useMemo(function() {
        // console.log(`[Avatar Debug - ${username}] Dicebear URL 생성 시도: seed='${safeSeed}', style='${style}'`);
        try {
            // 이제 style은 generateAvatarUrl이 기대하는 타입과 일치합니다.
            var url = generateAvatarUrl(safeSeed, style);
            // console.log(`[Avatar Debug - ${username}] Generated Dicebear URL: '${url}'`);
            return url;
        } catch (error) {
            console.error('[Avatar] Dicebear URL 생성 실패:', error);
            return null;
        }
    }, [
        safeSeed,
        style
    ]);
    var handleImageError = function(failedSrc) {
        console.warn("[Avatar Debug - ".concat(username, "] Image load failed for src: '").concat(failedSrc, "'. Falling back."));
        if (onError) onError();
    };
    var sourceSrc = profileImage && profileImage.trim() !== "" ? profileImage : null;
    var finalSrc = sourceSrc || dicebearFallbackUrl;
    // console.log(`[Avatar Debug - ${username}] Final src for ImageDisplay: '${finalSrc}', Fallback: '${DEFAULT_STATIC_FALLBACK}'`);
    var imageKey = useMemo(function() {
        var cleanSrc = finalSrc === null || finalSrc === void 0 ? void 0 : finalSrc.split('?')[0];
        return "avatar-".concat(username, "-").concat(cleanSrc || 'no-src');
    }, [
        finalSrc,
        username
    ]);
    var wrapperStyles = getAvatarStyles(size, border, borderColor);
    return /*#__PURE__*/ React.createElement("div", {
        className: className,
        style: wrapperStyles
    }, /*#__PURE__*/ React.createElement("div", {
        style: {
            width: '100%',
            height: '100%'
        }
    }, /*#__PURE__*/ React.createElement(ImageDisplay, {
        key: imageKey,
        src: finalSrc || '',
        fallbackSrc: DEFAULT_STATIC_FALLBACK,
        alt: alt || "".concat(displayName, "의 ").concat(sourceSrc ? '프로필' : '기본 아바타'),
        onError: function() {
            return handleImageError(finalSrc || 'unknown');
        },
        showErrorUI: false
    })));
};
export default Avatar;
